/**
 * CategoryIconsEditor Component
 * Grid editor for 8 category filter icons
 */

import React from 'react';
import { Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import type { CategoryIconConfig } from '../types';
import { readFileAsDataUrl } from '../utils';

interface CategoryIconsEditorProps {
    icons: CategoryIconConfig[];
    onChange: (icons: CategoryIconConfig[]) => void;
    accept?: string;
    disabled?: boolean;
}

export function CategoryIconsEditor({
    icons,
    onChange,
    accept = 'image/*',
    disabled = false,
}: CategoryIconsEditorProps) {
    const handleIconUpload = async (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
        if (!file.type.startsWith('image/') && !isSvg) {
            toast.error('กรุณาเลือกไฟล์รูปภาพ (PNG, JPG)');
            event.target.value = '';
            return;
        }

        try {
            const dataUrl = await readFileAsDataUrl(file);
            const updated = [...icons];
            if (updated[index]) {
                updated[index] = { ...updated[index], image: dataUrl };
                onChange(updated);
            }
        } catch (error) {
            console.error('Failed to read category icon file', error);
            toast.error('ไม่สามารถอ่านไฟล์ได้');
        } finally {
            event.target.value = '';
        }
    };

    const handleIconClear = (index: number) => {
        const updated = [...icons];
        if (updated[index]) {
            updated[index] = { ...updated[index], image: '' };
            onChange(updated);
        }
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {icons.map((icon, index) => (
                    <div key={icon.id} className="flex flex-col items-center">
                        {/* Image Upload Area */}
                        <div className="relative w-full aspect-square max-w-[120px] rounded-xl border-2 border-dashed border-stone-300 dark:border-stone-600 bg-stone-50 dark:bg-stone-800 overflow-hidden group hover:border-yellow-500 dark:hover:border-orange-500 transition-colors">
                            {icon.image ? (
                                <>
                                    <img
                                        src={icon.image}
                                        alt={icon.title}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                        <label className="cursor-pointer px-2 py-1 bg-white/90 rounded text-xs font-medium text-stone-700 hover:bg-white">
                                            เปลี่ยน
                                            <input
                                                type="file"
                                                accept={accept}
                                                onChange={(e) => handleIconUpload(index, e)}
                                                disabled={disabled}
                                                className="hidden"
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => handleIconClear(index)}
                                            disabled={disabled}
                                            className="px-2 py-1 bg-red-500/90 rounded text-xs font-medium text-white hover:bg-red-600"
                                        >
                                            ลบ
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <label className="cursor-pointer w-full h-full flex flex-col items-center justify-center text-stone-400 dark:text-stone-500 hover:text-yellow-600 dark:hover:text-orange-500 transition-colors">
                                    <Plus className="h-8 w-8 mb-1" />
                                    <span className="text-[10px] font-medium">อัปโหลด</span>
                                    <input
                                        type="file"
                                        accept={accept}
                                        onChange={(e) => handleIconUpload(index, e)}
                                        disabled={disabled}
                                        className="hidden"
                                    />
                                </label>
                            )}
                        </div>
                        {/* Category Title */}
                        <span className="mt-2 text-xs font-medium text-stone-700 dark:text-stone-300 text-center line-clamp-2">
                            {icon.title}
                        </span>
                    </div>
                ))}
            </div>

            {/* Info Box */}
            <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                    💡 รูปที่อัปโหลดจะแสดงในหน้า Home ของแอป หากไม่อัปโหลดจะใช้รูป default
                </p>
            </div>
        </div>
    );
}
